import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, FolderKanban } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { useProjects, useProjectMutations } from '../hooks/useProjects';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { ProjectsFilterBar } from '../components/ProjectsFilterBar';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectFormModal } from '../components/ProjectFormModal';
import { ProjectDrawer } from '../components/ProjectDrawer';
import type { Project, CreateProjectPayload, UpdateProjectPayload } from '../types';

export function ProjectsPage() {
  const user = useUser();
  const navigate = useNavigate();
  const role = user?.role ?? '';

  // ─── Role gating (inline, mirrors backend) ──────────────────────────────────
  const isPrivileged = ['admin', 'superadmin'].includes(role); // dept filter
  const canManage = ['manager', 'admin', 'superadmin'].includes(role); // create / edit
  // Delete is intentionally hidden for the demo (backend cascade-hook defect).

  // ─── Filters ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('');

  const filters = useMemo(() => (dept ? { dept } : {}), [dept]);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { data: projects = [], isLoading, isError } = useProjects(filters);
  const { data: users = [] } = useUserOptions();
  const { createProject, updateProject } = useProjectMutations();

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [drawerProject, setDrawerProject] = useState<Project | null>(null);

  // A project_assigned notification links here with ?projectId= — consumed
  // once the project is found in the loaded list, then stripped from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const projectId = searchParams.get('projectId');
    if (!projectId || projects.length === 0) return;
    const found = projects.find((p) => p.id === projectId);
    if (found) {
      setDrawerProject(found);
      const next = new URLSearchParams(searchParams);
      next.delete('projectId');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, projects, setSearchParams]);

  // ─── Client-side search: name / description / client ─────────────────────────
  const visibleProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      [p.name, p.description, p.client].some((field) => (field ?? '').toLowerCase().includes(q)),
    );
  }, [projects, search]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditProject(null);
    setFormOpen(true);
  };

  const handleEdit = useCallback((project: Project) => {
    setEditProject(project);
    setFormOpen(true);
  }, []);

  const handleOpenDrawer = useCallback((project: Project) => {
    setDrawerProject(project);
  }, []);

  const handleViewTasks = useCallback(
    (_project: Project) => {
      // Tasks filtering is internal to the Tasks page (not URL-driven), so we
      // navigate safely to the Tasks board without exposing broken state.
      navigate(ROUTES.tasks);
    },
    [navigate],
  );

  const handleFormSubmit = (payload: CreateProjectPayload | UpdateProjectPayload) => {
    if (editProject) {
      updateProject.mutate(
        { id: editProject.id, payload: payload as UpdateProjectPayload },
        {
          onSuccess: () => {
            toast.success('Project updated.');
            setFormOpen(false);
            setEditProject(null);
          },
          onError: () => toast.error('Failed to update project.'),
        },
      );
    } else {
      createProject.mutate(payload as CreateProjectPayload, {
        onSuccess: () => {
          toast.success('Project created.');
          setFormOpen(false);
        },
        onError: () => toast.error('Failed to create project.'),
      });
    }
  };

  const handleDrawerSave = (id: string, payload: UpdateProjectPayload) => {
    updateProject.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Project updated.');
          setDrawerProject(null);
        },
        onError: () => toast.error('Failed to update project.'),
      },
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-4 pb-16">
      {/* Header */}
      <PageHeader
        icon={FolderKanban}
        eyebrow="Workspace"
        title="Projects"
        description={<>{visibleProjects.length} project{visibleProjects.length !== 1 ? 's' : ''}</>}
        actions={
          <>{canManage && (
                  <Button onClick={handleCreate} size="md">
                    <Plus className="h-4 w-4" />
                    New Project
                  </Button>
                )}</>
        }
      />

      {/* Filters */}
      <ProjectsFilterBar
        search={search}
        dept={dept}
        showDeptFilter={isPrivileged}
        onSearchChange={setSearch}
        onDeptChange={setDept}
      />

      {/* Grid */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState
          title="Failed to load projects"
          description="Check your connection or try refreshing."
        />
      ) : visibleProjects.length === 0 ? (
        <EmptyState
          title={search ? 'No matching projects' : 'No projects yet'}
          description={
            search ? 'Try a different search term.' : 'Projects will appear here once created.'
          }
          action={canManage && !search ? <Button onClick={handleCreate}>New Project</Button> : undefined}
        />
      ) : (
        <div className="grid flex-1 auto-rows-min grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2 xl:grid-cols-3">
          {visibleProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              canEdit={canManage}
              onOpen={handleOpenDrawer}
              onEdit={handleEdit}
              onViewTasks={handleViewTasks}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <ProjectFormModal
        open={formOpen}
        project={editProject}
        users={users}
        defaultDepartment={user?.department}
        loading={createProject.isPending || updateProject.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditProject(null);
        }}
        onSubmit={handleFormSubmit}
      />

      {/* Detail Drawer */}
      <ProjectDrawer
        project={drawerProject}
        users={users}
        canEdit={canManage}
        saving={updateProject.isPending}
        onClose={() => setDrawerProject(null)}
        onSave={handleDrawerSave}
      />
    </div>
  );
}
